import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";

interface HeroCarouselProps {
  movies: Movie[];
  onAction: (message: string) => void;
}

export default function HeroCarousel({ movies, onAction }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (movies.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % movies.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [movies.length]);

  if (movies.length === 0) return null;
  const current = movies[index];

  return (
    <section className="relative w-full overflow-hidden bg-foreground-950">
      <div className="relative h-[440px] w-full md:h-[580px]">
        {movies.map((movie, i) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={movie.banner || movie.cover}
              alt={`${movie.title} 焦点海报`}
              title={`${movie.title} 熊猫影院`}
              className="h-full w-full object-cover object-center"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/30"></div>

        <div className="relative mx-auto flex h-full max-w-[1280px] flex-col justify-end px-4 pb-14 md:px-6 md:pb-20">
          <div key={current.id} className="w-full max-w-2xl animate-fade-up">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                {current.typeLabel}
              </span>
              <span className="flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-foreground-950">
                <i className="ri-star-fill text-[10px]"></i>
                {current.rating.toFixed(1)}
              </span>
              <span className="text-xs text-background-200">
                {current.year} · {current.region} · {current.genres.join(" / ")}
              </span>
            </div>
            <h2 className="font-heading text-3xl font-bold leading-tight text-white md:text-5xl">
              {current.title}
            </h2>
            <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
              {current.summary}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
              <button
                type="button"
                onClick={() => onAction("收藏功能尚未接入，当前为前端演示")}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-background-50/20 cursor-pointer"
              >
                <i className="ri-bookmark-line text-lg"></i>
                加入收藏
              </button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {movies.map((movie, i) => (
            <button
              key={movie.id}
              type="button"
              aria-label={`切换到 ${movie.title}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-8 bg-primary-400" : "w-2.5 bg-background-50/50 hover:bg-background-50/80"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}